import { useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Phone, ChevronRight, User, Key, Repeat } from 'lucide-react';
import { StatusSlider } from './StatusSlider';
import { format } from 'date-fns';

export const JobCard = ({ job, onStatusChange }) => {
  const navigate = useNavigate();
  
  const statusColors = {
    quote: 'bg-blue-500',
    scheduled: 'bg-amber-500',
    invoiced: 'bg-purple-500',
    paid: 'bg-emerald-500'
  };
  
  const statusLabels = {
    quote: 'Quote',
    scheduled: 'Scheduled',
    invoiced: 'Invoiced',
    paid: 'Paid'
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const handleCall = (e) => {
    e.stopPropagation();
    if (job.client_phone) {
      window.location.href = `tel:${job.client_phone}`;
    }
  };

  const handleMap = (e) => {
    e.stopPropagation();
    if (job.address) {
      window.open(`https://maps.google.com/?q=${encodeURIComponent(job.address)}`, '_blank');
    }
  };

  // Check if there are access details
  const hasAccessDetails = job.gate_code || job.lockbox_code || job.access_notes;

  return (
    <div 
      data-testid={`job-card-${job.id}`}
      className="job-card bg-white border-2 border-slate-200 rounded-xl shadow-hard overflow-hidden cursor-pointer"
      onClick={() => navigate(`/job/${job.id}`)}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <span className="font-heading text-sm font-bold text-slate-500 tracking-wide">
            #{job.id.slice(0, 8).toUpperCase()}
          </span>
          {/* Recurring indicator */}
          {job.parent_job_id && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
              <Repeat className="w-3 h-3" />
              Visit
            </span>
          )}
          {job.is_recurring && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
              <Repeat className="w-3 h-3" />
              Recurring
            </span>
          )}
        </div>
        {/* Don't show status badge for parent recurring jobs */}
        {!job.is_recurring && (
          <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${statusColors[job.status] || 'bg-slate-400'}`}>
            {statusLabels[job.status] || job.status}
          </span>
        )}
      </div>
      
      {/* Body */}
      <div className="p-4">
        <h3 className="font-heading text-2xl font-bold text-slate-dark mb-1">
          {job.client_name}
        </h3>
        
        <p className="text-slate-600 text-sm mb-3 line-clamp-2">
          {job.description}
        </p>
        
        <div className="space-y-2 mb-4">
          {job.full_address && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{job.full_address}</span>
            </div>
          )}
          {job.scheduled_date && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Calendar className="w-4 h-4 flex-shrink-0" />
              <span>{formatDate(job.scheduled_date)}</span>
            </div>
          )}
          {/* Assigned User */}
          {job.assigned_to_name && (
            <div className="flex items-center gap-2 text-blue-600 text-sm">
              <User className="w-4 h-4 flex-shrink-0" />
              <span className="font-medium">{job.assigned_to_name}</span>
            </div>
          )}
        </div>

        {/* Access Details */}
        {hasAccessDetails && (
          <div className="mb-4 p-2 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-start gap-2">
              <Key className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-amber-800">
                {job.gate_code && <span><strong>Gate:</strong> {job.gate_code}</span>}
                {job.lockbox_code && <span><strong>Lockbox:</strong> {job.lockbox_code}</span>}
                {job.access_notes && <span className="w-full truncate"><strong>Access:</strong> {job.access_notes}</span>}
              </div>
            </div>
          </div>
        )}
        
        {/* Amount and Quick Actions */}
        <div className="flex items-center justify-between mb-4">
          <span className="font-heading text-3xl font-bold text-slate-dark">
            ${job.quote_amount.toLocaleString()}
          </span>
          
          <div className="flex gap-2">
            {job.client_phone && (
              <button
                data-testid={`job-call-${job.id}`}
                onClick={handleCall}
                className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
              >
                <Phone className="w-5 h-5 text-slate-600" />
              </button>
            )}
            {job.full_address && (
              <button
                data-testid={`job-map-${job.id}`}
                onClick={handleMap}
                className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
              >
                <MapPin className="w-5 h-5 text-slate-600" />
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Status Slider - Only for non-parent recurring jobs */}
      {!job.is_recurring && (
        <div className="px-4 pb-4" onClick={(e) => e.stopPropagation()}>
          <StatusSlider 
            status={job.status} 
            onChange={(newStatus) => onStatusChange(job.id, newStatus)} 
          />
        </div>
      )}
      
      {/* Recurring Schedule Info - For parent recurring jobs */}
      {job.is_recurring && (
        <div className="px-4 pb-4">
          <div className="bg-purple-50 rounded-lg p-3 border border-purple-200 text-center">
            <p className="text-purple-700 font-medium text-sm">
              {job.recurrence_pattern === 'weekly' ? 'Weekly' : 
               job.recurrence_pattern === 'biweekly' ? 'Every 2 weeks' : 
               job.recurrence_pattern === 'monthly' ? 'Monthly' : job.recurrence_pattern} recurring job
            </p>
            <p className="text-purple-500 text-xs mt-1">Tap to view scheduled visits</p>
          </div>
        </div>
      )}
      
      {/* View Details Hint */}
      <div className="px-4 py-2 bg-slate-50 flex items-center justify-center text-slate-400 text-sm">
        <span>Tap for details</span>
        <ChevronRight className="w-4 h-4 ml-1" />
      </div>
    </div>
  );
};

export default JobCard;
